import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Linking,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { Text } from "../../../components/shared/Themed";
import { AntDesign, MaterialCommunityIcons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/native";
import { validSignedURL } from "../../../utils/validateSignedUrl";
import { useTranslation } from "../../../utils/useTranslation";
import ChinaShipmentEditFormModal from "./ChinaShipmentEditFormModal";
import { useSelector } from "react-redux";
import teamsConfig from "../../../utils/constants/teams";
import rolesConfig from "../../../utils/constants/roles";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

// ─── Theme ────────────────────────────────────────────────────────────────────
const C = {
  bg: "#F0F2F5",
  card: "#FFFFFF",
  border: "#DDE1E8",
  borderLight: "#ECEEF2",
  navy: "#1A2B4A",
  navyLight: "#2C3E6B",
  accent: "#2563EB",
  accentLight: "#EEF3FD",
  label: "#64748B",
  value: "#1E293B",
  divider: "#E8EBF0",
  tagBg: "#F1F5FE",
  tagText: "#2563EB",
  muted: "#94A3B8",
  success: "#059669",
  successBg: "#ECFDF5",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
};

const fetchImageForPDF = async (url: string): Promise<ArrayBuffer | null> => {
  try {
    const res = await fetch(url, {});
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.arrayBuffer();
  } catch (err) {
    console.warn(
      `[PDF Export] Could not fetch image for embedding: ${url}`,
      err,
    );
    return null;
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader = ({ title }: { title: string }) => (
  <View style={styles.sectionHeader}>
    <View style={styles.sectionHeaderAccent} />
    <Text style={styles.sectionHeaderText}>{title}</Text>
  </View>
);

const InfoRow = ({
  label,
  value,
  noBorder,
  highlight,
}: {
  label: string;
  value: string;
  noBorder?: boolean;
  highlight?: boolean;
}) => (
  <View style={[styles.infoRow, noBorder && { borderBottomWidth: 0 }]}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={[styles.infoValue, highlight && styles.infoValueHighlight]}>
      {value || "—"}
    </Text>
  </View>
);

const Badge = ({
  text,
  variant = "default",
}: {
  text: string;
  variant?: "default" | "success" | "navy";
}) => {
  const variantStyle =
    variant === "success"
      ? { bg: C.successBg, fg: C.success }
      : variant === "navy"
        ? { bg: C.navyLight, fg: "#FFFFFF" }
        : { bg: C.tagBg, fg: C.tagText };
  return (
    <View style={[styles.badge, { backgroundColor: variantStyle.bg }]}>
      <Text style={[styles.badgeText, { color: variantStyle.fg }]}>{text}</Text>
    </View>
  );
};

const QtyRow = ({
  label,
  perCarton,
  cartons,
  total,
  unit,
}: {
  label: string;
  perCarton: number;
  cartons: number;
  total: number;
  unit: string;
}) => (
  <View style={styles.qtyRow}>
    <Text style={styles.qtyLabel}>{label}</Text>
    <View style={styles.qtyFormula}>
      <View style={styles.qtyBox}>
        <Text style={styles.qtyBoxText}>
          {perCarton?.toLocaleString() ?? "—"}
        </Text>
      </View>
      <Text style={styles.qtyUnit}>{unit}/ctn</Text>
      <Text style={styles.qtyOp}>×</Text>
      <View style={styles.qtyBox}>
        <Text style={styles.qtyBoxText}>
          {cartons?.toLocaleString() ?? "—"}
        </Text>
      </View>
      <Text style={styles.qtyUnit}>ctns</Text>
      <Text style={styles.qtyOp}>=</Text>
      <View style={[styles.qtyBox, styles.qtyBoxResult]}>
        <Text style={[styles.qtyBoxText, styles.qtyBoxResultText]}>
          {total?.toLocaleString() ?? "—"}
        </Text>
      </View>
      <Text style={[styles.qtyUnit, { color: C.accent, fontWeight: "600" }]}>
        {unit === "Kg" ? "kg" : "pcs"}
      </Text>
    </View>
  </View>
);

const ItemCard = ({
  item,
  index,
  renderImage,
  t,
}: {
  item: any;
  index: number;
  renderImage: (itemCode: string) => React.ReactNode;
  t: any;
}) => (
  <View style={styles.itemCard}>
    {/* Card header */}
    <View style={styles.itemCardHeader}>
      <View style={styles.itemIndexBadge}>
        <Text style={styles.itemIndexText}>
          {String(index + 1).padStart(2, "0")}
        </Text>
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.itemTitle}>{item.itemCode}</Text>
        {item.itemDescription ? (
          <Text style={styles.itemSubtitle} numberOfLines={1}>
            {item.itemDescription}
          </Text>
        ) : null}
      </View>
      {item.label ? <Badge text={item.label} variant="navy" /> : null}
    </View>

    <View style={styles.itemDivider} />

    {/* Meta grid */}
    <View style={styles.metaGrid}>
      <View style={styles.metaCol}>
        <InfoRow label={t("itemCode")} value={item.chinaItemCode} />
        <InfoRow label={t("itemDescription")} value={item.itemDescription} />
        <InfoRow label={t("material")} value={item.material} noBorder />
      </View>
      <View style={styles.metaDivider} />
      <View style={styles.metaCol}>
        <InfoRow label={t("orderNumber")} value={item.orderNumber} highlight />
        <InfoRow label={t("label")} value={item.label} />
        <InfoRow label={t("spec")} value={item.spec} noBorder />
      </View>
    </View>

    <View style={styles.itemDivider} />

    {/* Qty + image */}
    <View style={styles.qtyAndImage}>
      <View style={styles.qtyRows}>
        <QtyRow
          label={t("quantity")}
          perCarton={item.qtyPerCarton}
          cartons={item.qtyCartons}
          total={item.qtyTotal}
          unit="pcs"
        />
        <QtyRow
          label={t("grossWeight")}
          perCarton={item.gwPerCarton}
          cartons={item.gwCartons}
          total={item.gwTotal}
          unit="Kg"
        />
        <QtyRow
          label={t("netWeight")}
          perCarton={item.nwPerCarton}
          cartons={item.nwCartons}
          total={item.nwTotal}
          unit="Kg"
        />
        {item.notes ? (
          <View style={styles.notesRow}>
            <MaterialCommunityIcons
              name="note-outline"
              size={13}
              color={C.muted}
            />
            <Text style={styles.notesLabel}>{t("notes")}: </Text>
            <Text style={styles.notesValue}>{item.notes}</Text>
          </View>
        ) : null}
      </View>

      {renderImage(
        item.factoryOrderDetail?.trackerOrder?.ItemCode ?? item.itemCode,
      )}
    </View>
  </View>
);

// ─── Main screen ──────────────────────────────────────────────────────────────
const ChinaShipmentDetailsReport = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { shipment } = route.params as { shipment: any };
  const { t } = useTranslation();

  const { userInfo, currentRole } = useSelector(
    (state: any) => state.configurations,
  );
  const isAdminOrOwner = [
    rolesConfig.roles.admin,
    rolesConfig.roles.owner,
  ].includes(currentRole?.toUpperCase());
  const userTeam = userInfo?.team?.toUpperCase();
  const canAccessEdit =
    [teamsConfig.teams.purchasing].includes(userTeam) || isAdminOrOwner;

  const chinaShipments = shipment.items;
  const [images, setImages] = useState<Record<string, string>>({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<any>(null);

  useEffect(() => {
    if (!chinaShipments?.length) return;
    const uniqueItemCodes = [
      ...new Set(
        chinaShipments.map(
          (item: any) =>
            item.factoryOrderDetail?.trackerOrder?.ItemCode ?? item.itemCode,
        ),
      ),
    ] as string[];

    uniqueItemCodes.forEach((itemCode: string) => {
      if (!itemCode || images[itemCode]) return;
      setLoadingMap((prev) => ({ ...prev, [itemCode]: true }));
      validSignedURL(
        itemCode,
        "label",
        () => {},
        (url: string) => {
          setImages((prev) => ({ ...prev, [itemCode]: url }));
          setLoadingMap((prev) => ({ ...prev, [itemCode]: false }));
        },
      );
    });
  }, [chinaShipments]);

  // ── Product Image Component ──────────────────────────────────────────────
  const ProductImage = ({ itemCode }: { itemCode: string }) => {
    const imageUrl = images[itemCode];

    const handleOpen = async () => {
      if (!imageUrl) return;
      const supported = await Linking.canOpenURL(imageUrl);
      if (supported) await Linking.openURL(imageUrl);
    };

    return (
      <TouchableOpacity activeOpacity={0.85} onPress={handleOpen}>
        <View style={styles.imageContainer}>
          {loadingMap[itemCode] && (
            <View style={styles.imageLoaderWrapper}>
              <ActivityIndicator size="small" color={C.accent} />
              <Text style={styles.imageLoadingText}>Loading...</Text>
            </View>
          )}
          {imageUrl && !loadingMap[itemCode] && (
            <Image
              source={{ uri: imageUrl }}
              style={styles.productImage}
              resizeMode="contain"
              // @ts-ignore
              crossOrigin="anonymous"
            />
          )}
          {!loadingMap[itemCode] && !imageUrl && (
            <View style={styles.imagePlaceholder}>
              <MaterialCommunityIcons
                name="image-off-outline"
                size={28}
                color={C.muted}
              />
              <Text style={styles.imagePlaceholderText}>No Image</Text>
            </View>
          )}
          {imageUrl && (
            <View style={styles.imageOpenHint}>
              <MaterialCommunityIcons
                name="open-in-new"
                size={11}
                color="#fff"
              />
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // ── PDF Export ───────────────────────────────────────────────────────────
  const handleExportAsPDF = async (shipment: any) => {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    let page = pdfDoc.addPage();
    const { width, height } = page.getSize();
    let cursorY = height - 40;

    const drawText = (
      text: string,
      x: number,
      y: number,
      size = 10,
      bold = false,
      color = rgb(0.12, 0.18, 0.29),
    ) => {
      page.drawText(String(text ?? "—"), {
        x,
        y,
        size,
        font: bold ? fontBold : font,
        color,
      });
    };

    const ensureSpace = (needed: number) => {
      if (cursorY - needed < 40) {
        page = pdfDoc.addPage();
        cursorY = height - 40;
      }
    };

    // Title
    drawText(
      `Shipment Report — Container: ${shipment.containerNo}`,
      30,
      cursorY,
      15,
      true,
      rgb(0.1, 0.16, 0.4),
    );
    cursorY -= 28;

    // Horizontal rule
    page.drawLine({
      start: { x: 30, y: cursorY },
      end: { x: width - 30, y: cursorY },
      thickness: 1,
      color: rgb(0.87, 0.88, 0.91),
    });
    cursorY -= 18;

    // Container info two columns
    const infoLeft = [
      ["Vessel/Voyage", shipment.vesselVoyage],
      ["Shipping Line", shipment.shippingLine],
      ["Sea Freight", shipment.seaFreight],
      ["Freight Line", shipment.freightLine],
    ];
    const infoRight = [
      ["Seal No", shipment.sealNo],
      ["ETD", formatDate(shipment.ETD)],
      ["ETA", formatDate(shipment.ETA)],
      ["Location", shipment.location],
    ];
    const colL = 30,
      colR = width / 2 + 10;
    for (let i = 0; i < infoLeft.length; i++) {
      drawText(
        `${infoLeft[i][0]}:`,
        colL,
        cursorY,
        9,
        true,
        rgb(0.4, 0.45, 0.56),
      );
      drawText(`${infoLeft[i][1] || "—"}`, colL + 80, cursorY, 9);
      drawText(
        `${infoRight[i][0]}:`,
        colR,
        cursorY,
        9,
        true,
        rgb(0.4, 0.45, 0.56),
      );
      drawText(`${infoRight[i][1] || "—"}`, colR + 65, cursorY, 9);
      cursorY -= 15;
    }
    cursorY -= 14;

    // Items
    for (const item of shipment.items) {
      const itemCode =
        item.factoryOrderDetail?.trackerOrder?.ItemCode || item.itemCode;
      ensureSpace(120);

      // Item header
      page.drawRectangle({
        x: 28,
        y: cursorY - 4,
        width: width - 56,
        height: 18,
        color: rgb(0.93, 0.95, 0.99),
      });
      drawText(itemCode, 34, cursorY + 2, 11, true, rgb(0.1, 0.16, 0.4));
      cursorY -= 22;

      const metaLeft = [
        ["Item Code", item.chinaItemCode],
        ["Material", item.material],
      ];
      const metaRight = [
        ["Order No", item.orderNumber],
        ["Label", item.label],
        ["Spec", item.spec],
      ];

      // Draw description full width first
      drawText(
        "Description:",
        colL + 8,
        cursorY,
        9,
        true,
        rgb(0.4, 0.45, 0.56),
      );
      drawText(`${item.itemDescription || "—"}`, colL + 70, cursorY, 9);
      cursorY -= 13;

      for (let i = 0; i < metaLeft.length; i++) {
        drawText(
          `${metaLeft[i][0]}:`,
          colL + 8,
          cursorY,
          9,
          true,
          rgb(0.4, 0.45, 0.56),
        );
        drawText(`${metaLeft[i][1] || "—"}`, colL + 70, cursorY, 9);
        drawText(
          `${metaRight[i][0]}:`,
          colR + 8,
          cursorY,
          9,
          true,
          rgb(0.4, 0.45, 0.56),
        );
        drawText(`${metaRight[i][1] || "—"}`, colR + 65, cursorY, 9);
        cursorY -= 13;
      }

      const qtyRows = [
        [
          "Qty",
          `${item.qtyPerCarton} × ${item.qtyCartons} = ${item.qtyTotal} pcs`,
        ],
        [
          "Gross Weight",
          `${item.gwPerCarton} × ${item.gwCartons} = ${item.gwTotal} kg`,
        ],
        [
          "Net Weight",
          `${item.nwPerCarton} × ${item.nwCartons} = ${item.nwTotal} kg`,
        ],
      ];
      for (const [label, value] of qtyRows) {
        drawText(`${label}:`, colL + 8, cursorY, 9, true, rgb(0.4, 0.45, 0.56));
        drawText(value, colL + 80, cursorY, 9, false, rgb(0.15, 0.37, 0.92));
        cursorY -= 13;
      }

      if (item.notes) {
        drawText("Notes:", colL + 8, cursorY, 9, true, rgb(0.4, 0.45, 0.56));
        drawText(item.notes, colL + 46, cursorY, 9);
        cursorY -= 13;
      }

      // Embed image — CORS-safe fetch
      if (images[itemCode]) {
        const imgBytes = await fetchImageForPDF(images[itemCode]);
        if (imgBytes) {
          try {
            const pngImage = await pdfDoc.embedPng(imgBytes).catch(async () => {
              // Try JPEG if PNG fails
              return await pdfDoc.embedJpg(imgBytes);
            });
            const scaled = pngImage.scaleToFit(100, 100);
            ensureSpace(scaled.height + 10);
            page.drawImage(pngImage, {
              x: colR + 8,
              y: cursorY - scaled.height,
              width: scaled.width,
              height: scaled.height,
            });
            cursorY = Math.min(cursorY, cursorY - scaled.height);
          } catch (err) {
            console.warn(`[PDF] Image embed failed for ${itemCode}`, err);
          }
        }
      }

      cursorY -= 16;
      page.drawLine({
        start: { x: 30, y: cursorY },
        end: { x: width - 30, y: cursorY },
        thickness: 0.5,
        color: rgb(0.87, 0.88, 0.91),
      });
      cursorY -= 12;
    }

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `Shipment_${shipment.containerNo}.pdf`;
    link.click();
  };

  const handleEdit = (shipment: any) => {
    setSelectedShipment(shipment);
    setEditModalVisible(true);
  };

  return (
    <>
      <ChinaShipmentEditFormModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        shipment={selectedShipment}
      />

      <View style={styles.screen}>
        {/* ── Header ── */}
        <View style={styles.headerBar}>
          <TouchableOpacity
            onPress={() =>
              navigation.navigate("Root", {
                screen: "Home",
                params: {
                  screen: "Tracker",
                  params: {
                    screen: "ChinaShipmentReport",
                    params: { screen: "ChinaShipmentScreen" },
                  },
                },
              })
            }
            style={styles.backBtn}
          >
            <AntDesign name="arrowleft" size={18} color={C.navy} />
          </TouchableOpacity>

          <View style={styles.headerCenter}>
            <Text style={styles.headerEyebrow}>Shipment Details</Text>
            <Text style={styles.headerTitle}>
              {t("containerNo")}: {shipment.containerNo}
            </Text>
          </View>

          {canAccessEdit && (
            <View style={styles.headerActions}>
              <TouchableOpacity
                style={styles.headerActionBtn}
                onPress={() => handleEdit(shipment)}
              >
                <MaterialCommunityIcons
                  name="pencil-outline"
                  size={17}
                  color={C.accent}
                />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.headerActionBtn, styles.headerActionBtnPrimary]}
                onPress={() => handleExportAsPDF(shipment)}
              >
                <MaterialCommunityIcons
                  name="export-variant"
                  size={17}
                  color="#fff"
                />
                <Text style={styles.headerActionBtnText}>Export PDF</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* ── Stats strip ── */}
          <View style={styles.statsStrip}>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>
                {shipment.items?.length ?? 0}
              </Text>
              <Text style={styles.statLabel}>Items</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={styles.statValue}>
                {shipment.items
                  ?.reduce((s: number, i: any) => s + (i.qtyTotal || 0), 0)
                  .toLocaleString()}
              </Text>
              <Text style={styles.statLabel}>Total Pcs</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={styles.statValue}>
                {shipment.items
                  ?.reduce((s: number, i: any) => s + (i.gwTotal || 0), 0)
                  .toLocaleString()}
              </Text>
              <Text style={styles.statLabel}>Gross kg</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: C.accent }]}>
                {formatDate(shipment.ETA)}
              </Text>
              <Text style={styles.statLabel}>ETA</Text>
            </View>
          </View>

          {/* ── Container info card ── */}
          <View style={styles.sectionWrapper}>
            <SectionHeader title="Container Information" />
            <View style={styles.containerCard}>
              <View style={styles.metaGrid}>
                <View style={styles.metaCol}>
                  <InfoRow
                    label={t("vesselVoyage")}
                    value={shipment.vesselVoyage}
                  />
                  <InfoRow
                    label={t("shippingLine")}
                    value={shipment.shippingLine}
                  />
                  <InfoRow
                    label={t("seaFreight")}
                    value={shipment.seaFreight}
                  />
                  <InfoRow
                    label={t("freightLine")}
                    value={shipment.freightLine}
                    noBorder
                  />
                </View>
                <View style={styles.metaDivider} />
                <View style={styles.metaCol}>
                  <InfoRow label={t("sealNo")} value={shipment.sealNo} />
                  <InfoRow
                    label={t("etd")}
                    value={formatDate(shipment.ETD)}
                    highlight
                  />
                  <InfoRow
                    label={t("eta")}
                    value={formatDate(shipment.ETA)}
                    highlight
                  />
                  <InfoRow
                    label={t("location")}
                    value={shipment.location}
                    noBorder
                  />
                </View>
              </View>
            </View>
          </View>

          {/* ── Item cards ── */}
          <View style={styles.sectionWrapper}>
            <SectionHeader title={`Items (${shipment.items?.length ?? 0})`} />
            {shipment.items.map((item: any, index: number) => (
              <ItemCard
                key={item._id}
                item={item}
                index={index}
                renderImage={(itemCode) => <ProductImage itemCode={itemCode} />}
                t={t}
              />
            ))}
          </View>

          <View style={{ height: 40 }} />
        </ScrollView>
      </View>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: C.bg,
  },

  // Header
  headerBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: C.card,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 10,
    shadowColor: "#000",
    shadowOpacity: 0.04,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  backBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: C.border,
    justifyContent: "center",
    alignItems: "center",
  },
  headerCenter: {
    flex: 1,
  },
  headerEyebrow: {
    fontSize: 10,
    fontWeight: "600",
    color: C.muted,
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: C.navy,
    marginTop: 1,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerActionBtn: {
    height: 34,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: C.border,
    justifyContent: "center",
    alignItems: "center",
  },
  headerActionBtnPrimary: {
    backgroundColor: C.accent,
    borderColor: C.accent,
    flexDirection: "row",
    gap: 5,
  },
  headerActionBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#fff",
  },

  scrollContent: {
    paddingHorizontal: 14,
    paddingTop: 14,
    gap: 14,
  },

  // Stats strip
  statsStrip: {
    backgroundColor: C.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: C.border,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  statItem: {
    flex: 1,
    alignItems: "center",
  },
  statValue: {
    fontSize: 15,
    fontWeight: "700",
    color: C.navy,
  },
  statLabel: {
    fontSize: 10,
    color: C.muted,
    fontWeight: "500",
    marginTop: 2,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  statDivider: {
    width: 1,
    height: 28,
    backgroundColor: C.divider,
  },

  // Section wrapper
  sectionWrapper: {
    gap: 8,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 2,
  },
  sectionHeaderAccent: {
    width: 3,
    height: 14,
    borderRadius: 2,
    backgroundColor: C.accent,
  },
  sectionHeaderText: {
    fontSize: 12,
    fontWeight: "700",
    color: C.navy,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },

  // Container card
  containerCard: {
    backgroundColor: C.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: C.border,
    padding: 16,
  },

  // Shared grid
  metaGrid: {
    flexDirection: "row",
  },
  metaCol: {
    flex: 1,
  },
  metaDivider: {
    width: 1,
    backgroundColor: C.divider,
    marginHorizontal: 14,
  },

  // Info row
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: C.borderLight,
    gap: 8,
  },
  infoLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: C.label,
    flexShrink: 0,
    maxWidth: "45%",
  },
  infoValue: {
    fontSize: 11,
    fontWeight: "500",
    color: C.value,
    textAlign: "right",
    flexShrink: 1,
    flexWrap: "wrap",
  },
  infoValueHighlight: {
    color: C.accent,
    fontWeight: "600",
  },

  // Badge
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 5,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.3,
  },

  // Item card
  itemCard: {
    backgroundColor: C.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: C.border,
    padding: 16,
    gap: 12,
    marginBottom: 2,
    shadowColor: C.navy,
    shadowOpacity: 0.03,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 1,
  },
  itemCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  itemIndexBadge: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: C.accentLight,
    justifyContent: "center",
    alignItems: "center",
  },
  itemIndexText: {
    fontSize: 12,
    fontWeight: "800",
    color: C.accent,
  },
  itemTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: C.navy,
  },
  itemSubtitle: {
    fontSize: 11,
    color: C.muted,
    marginTop: 1,
  },
  itemDivider: {
    height: 1,
    backgroundColor: C.divider,
  },

  // Qty + image
  qtyAndImage: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  qtyRows: {
    flex: 1,
    gap: 6,
  },
  qtyRow: {
    flexDirection: "column",
    gap: 4,
  },
  qtyLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: C.label,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  qtyFormula: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
  },
  qtyBox: {
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minWidth: 44,
    alignItems: "center",
    backgroundColor: "#F8FAFC",
  },
  qtyBoxResult: {
    borderColor: C.accent,
    backgroundColor: C.accentLight,
  },
  qtyBoxText: {
    fontSize: 12,
    fontWeight: "600",
    color: C.value,
  },
  qtyBoxResultText: {
    color: C.accent,
  },
  qtyUnit: {
    fontSize: 10,
    color: C.muted,
    fontWeight: "500",
  },
  qtyOp: {
    fontSize: 12,
    fontWeight: "700",
    color: C.label,
  },

  // Notes
  notesRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
    backgroundColor: "#FFFBEB",
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#FDE68A",
  },
  notesLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: "#92400E",
  },
  notesValue: {
    fontSize: 11,
    color: "#78350F",
    flex: 1,
  },

  // Product image
  imageContainer: {
    width: 160,
    height: 160,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: C.border,
    backgroundColor: "#F8FAFC",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  productImage: {
    width: "100%",
    height: "100%",
  },
  imageLoaderWrapper: {
    alignItems: "center",
    gap: 6,
  },
  imageLoadingText: {
    fontSize: 10,
    color: C.muted,
  },
  imagePlaceholder: {
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
  },
  imagePlaceholderText: {
    fontSize: 10,
    color: C.muted,
    fontWeight: "500",
  },
  imageOpenHint: {
    position: "absolute",
    bottom: 6,
    right: 6,
    backgroundColor: "rgba(37,99,235,0.75)",
    borderRadius: 4,
    padding: 3,
  },
});

export default ChinaShipmentDetailsReport;
